import htmx from 'htmx.org';

const privateUseStart = 0xE000;
const privateUseEnd = 0xF8FF;
const defaultSymbolMapUrl = '/Client/GetSymbolMap';
const symbolMaps = new Map<string, Promise<Record<string, string>>>();
const loadedSymbolMaps = new Map<string, Record<string, string>>();

/**
 * [data-convert-symbols] on a textarea converts private-use characters (U+E000-U+F8FF) in pasted text to standard Unicode.
 * Word stores characters typed in its Symbol and Wingdings fonts as private-use code points, which most browsers and PDF fonts
 * cannot draw. The conversion runs on paste, so the user sees the real character (for example a beta) instead of a missing-glyph box.
 * The lookup comes from the server, so there is nothing to supply: the component requests /Client/GetSymbolMap once per page
 * and shares it between every textarea. The request starts the first time a textarea gets focus, so the lookup is normally
 * loaded before anything is pasted and the conversion happens immediately. A textarea nobody focuses never makes the request.
 * If a paste with private-use characters arrives before the lookup has loaded, the paste waits for it and is inserted when it arrives.
 * A private-use character with no entry in the lookup is removed. If the lookup cannot be loaded, the text is pasted unchanged.
 * [data-convert-symbols] may hold a different URL to load the lookup from. It is a JSON object from private-use character to replacement text.
 * [maxlength] is still honored: the converted text is truncated to the space left, as a normal paste would be.
 * It is meant to be used with the <area-block> tag helper (convert-symbols="true") but works on any textarea.
 * Only paste is converted. The server repeats the conversion on save, so text that did not come through a paste is still covered.
 * @param content
 */
export function bindSymbolConversion(content?: Element | Document | null | undefined) {
    if (!content) {
        content = document;
    }

    content.querySelectorAll<HTMLTextAreaElement>('textarea[data-convert-symbols]').forEach(textarea => {
        if (textarea.getAttribute('convert-symbols-event') === 'true') {
            return;
        }
        textarea.setAttribute('convert-symbols-event', 'true');
        textarea.addEventListener('focus', () => loadSymbolMap(getSymbolMapUrl(textarea)).catch(error => console.debug('Unable to load the symbol map.', error)));
        textarea.addEventListener('paste', event => convertPaste(textarea, event));
    });
}

function convertPaste(textarea: HTMLTextAreaElement, event: ClipboardEvent) {
    const pasted = event.clipboardData?.getData('text/plain');
    if (!pasted || !containsPrivateUseCharacter(pasted)) {
        return;
    }

    // The clipboard and selection are only available now, so capture them before any waiting for the lookup.
    event.preventDefault();
    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;
    const url = getSymbolMapUrl(textarea);

    const loadedMappings = loadedSymbolMaps.get(url);
    if (loadedMappings) {
        insertText(textarea, convertPrivateUseCharacters(pasted, loadedMappings), selectionStart, selectionEnd);
        return;
    }

    loadSymbolMap(url)
        .then(mappings => insertText(textarea, convertPrivateUseCharacters(pasted, mappings), selectionStart, selectionEnd))
        .catch(error => {
            console.debug('Unable to load the symbol map.', error);
            insertText(textarea, pasted, selectionStart, selectionEnd);
        });
}

function getSymbolMapUrl(textarea: HTMLTextAreaElement): string {
    return textarea.getAttribute('data-convert-symbols') || defaultSymbolMapUrl;
}

function loadSymbolMap(url: string): Promise<Record<string, string>> {
    let symbolMap = symbolMaps.get(url);
    if (!symbolMap) {
        symbolMap = fetch(url)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Symbol map request failed with status ${response.status}.`);
                }
                return response.json() as Promise<Record<string, string>>;
            })
            .then(mappings => {
                loadedSymbolMaps.set(url, mappings);
                return mappings;
            })
            .catch(error => {
                symbolMaps.delete(url);
                throw error;
            });
        symbolMaps.set(url, symbolMap);
    }
    return symbolMap;
}

function insertText(textarea: HTMLTextAreaElement, text: string, selectionStart: number, selectionEnd: number) {
    const maxLength = Number.parseInt(textarea.getAttribute('maxlength') ?? '', 10);
    const room = Number.isNaN(maxLength) ? Infinity : maxLength - (textarea.value.length - (selectionEnd - selectionStart));
    textarea.setRangeText(truncate(text, room), selectionStart, selectionEnd, 'end');
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
    textarea.dispatchEvent(new Event('keyup', { bubbles: true }));
}

// Cutting a surrogate pair in half would leave an invalid lone surrogate, so a character that does not fit is left out whole.
function truncate(text: string, length: number): string {
    const truncated = text.slice(0, Math.max(length, 0));
    const lastCode = truncated.charCodeAt(truncated.length - 1);
    const splitsSurrogatePair = truncated.length < text.length && lastCode >= 0xD800 && lastCode <= 0xDBFF;
    return splitsSurrogatePair ? truncated.slice(0, -1) : truncated;
}

function isPrivateUseCharacter(character: string): boolean {
    const code = character.charCodeAt(0);
    return code >= privateUseStart && code <= privateUseEnd;
}

function containsPrivateUseCharacter(text: string): boolean {
    return Array.from(text).some(isPrivateUseCharacter);
}

export function convertPrivateUseCharacters(text: string, mappings: Record<string, string>): string {
    return Array.from(text, character => isPrivateUseCharacter(character) ? mappings[character] ?? '' : character).join('');
}

document.addEventListener('DOMContentLoaded', () => bindSymbolConversion(document));
htmx.onLoad(content => bindSymbolConversion(content as HTMLElement));
